import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { FaYoutube, FaInstagram, FaTiktok, FaLinkedinIn, FaFacebookF } from 'react-icons/fa6';
import SectionWrapper from './ui/SectionWrapper';
import { STEPS, IMAGES } from '../constants';

const StartVisual = () => <img src={IMAGES.sneaker} alt="Product input" className="w-full h-full object-cover" />;

const PlanVisual = () => (
  <div className="grid grid-cols-3 gap-1.5 p-2 h-full bg-slate-100">
    {[IMAGES.productAd, IMAGES.social, IMAGES.cinematic].map((src, i) => (
      <div key={i} className="flex flex-col gap-1">
        <img src={src} alt="" className="w-full flex-1 min-h-0 object-cover rounded" />
        <span className="h-1.5 rounded bg-slate-300" /><span className="h-1.5 w-2/3 rounded bg-slate-300" />
      </div>
    ))}
  </div>
);

const CreateVisual = () => (
  <div className="relative h-full bg-[#0d0a18]">
    <img src={IMAGES.studioPro} alt="" className="w-full h-3/4 object-cover" />
    <div className="absolute inset-x-2 bottom-2 flex gap-1">
      {[40, 25, 35, 20, 30].map((w, i) => <span key={i} style={{ width: `${w}%` }} className={`h-3 rounded-sm ${i % 2 ? 'bg-fuchsia-400/70' : 'bg-violet-400/70'}`} />)}
    </div>
  </div>
);

const DeliverVisual = () => (
  <div className="h-full flex flex-col justify-center gap-3 p-3 bg-slate-50">
    <div className="flex gap-2 justify-center text-white">
      {[[FaYoutube, 'bg-red-600'], [FaInstagram, 'bg-gradient-to-br from-amber-400 via-pink-500 to-purple-600'], [FaTiktok, 'bg-black'], [FaLinkedinIn, 'bg-sky-700'], [FaFacebookF, 'bg-blue-600']].map(([Icon, bg], i) => (
        <span key={i} className={`w-7 h-7 rounded-md ${bg} flex items-center justify-center`}><Icon size={13} /></span>
      ))}
    </div>
    <div className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 p-2">
      <img src={IMAGES.creators} alt="" className="w-9 h-9 rounded object-cover" />
      <span className="text-[11px] leading-tight"><b className="block text-ink">Published Post</b><span className="text-emerald-600">● Live on 5 channels</span></span>
    </div>
  </div>
);

const VISUALS = [StartVisual, PlanVisual, CreateVisual, DeliverVisual];

const HowItWorks = () => (
  <SectionWrapper id="how-it-works" eyebrow="How it works" title="From idea to final content in four simple steps." subtitle="A complete creative workflow, from concept to publishing." className="bg-slate-50">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
      {STEPS.map(({ no, title, desc, icon: Icon }, i) => {
        const Visual = VISUALS[i];
        return (
          <motion.div
            key={no}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            className="relative bg-white rounded-2xl border border-slate-200 p-5 flex flex-col"
            data-testid={`how-step-${no}`}
          >
            <div className="flex gap-3">
              <span className="w-10 h-10 shrink-0 rounded-xl bg-violet-50 text-brand flex items-center justify-center"><Icon size={20} /></span>
              <div>
                <span className="text-xs font-bold text-slate-400">{no}</span>
                <h3 className="font-display font-bold text-ink">{title}</h3>
                <p className="text-sm text-slate-500 mt-1 leading-snug">{desc}</p>
              </div>
            </div>
            <div className="mt-5 h-36 rounded-xl overflow-hidden border border-slate-100"><Visual /></div>
            {i < STEPS.length - 1 && (
              <ArrowRight size={18} className="hidden lg:block absolute -right-[26px] top-1/2 -translate-y-1/2 text-brand" />
            )}
          </motion.div>
        );
      })}
    </div>
  </SectionWrapper>
);

export default HowItWorks;
